/**
 * Scheduled admin email sends (`email-scheduled-sends/{scheduledSendId}`).
 *
 * A scheduled send is a saved `enqueueadminemails` request plus the time it
 * should run. Nothing about the audience is resolved when it is saved: the
 * contact lists are read, recipients claimed and referral codes registered
 * when it runs, exactly as for a send made at that moment. That keeps the
 * saved request small and cheap to cancel, and means a list edited between
 * scheduling and sending is sent as it stands at send time.
 *
 * Documents are top-level rather than nested under the course so one runner
 * can address them by id; `courseId` is what scopes them.
 *
 * @module
 */

import {z} from 'zod';

import {FirestoreTimestampSchema} from '../types';

// ============================================================================
// Status
// ============================================================================

/**
 * Lifecycle of a scheduled send.
 *
 * - `scheduled` — waiting for its time. The only state that can be cancelled
 *   or rescheduled.
 * - `sending` — the runner has taken it. It never goes back to `scheduled`:
 *   by then recipients may already be claimed and mail queued, so running it
 *   again could mail them twice.
 * - `sent` — the enqueue finished and the send's batches exist.
 * - `failed` — the enqueue threw before any batch was queued, or the send
 *   could not run at all.
 * - `cancelled` — an admin cancelled it before it ran.
 */
export const EmailScheduledSendStatusSchema = z.enum([
  'scheduled',
  'sending',
  'sent',
  'failed',
  'cancelled',
]);

/** Inferred type for {@link EmailScheduledSendStatusSchema}. */
export type EmailScheduledSendStatus = z.infer<
  typeof EmailScheduledSendStatusSchema
>;

/** The admin who took an action on a scheduled send. */
const ActorSchema = z.object({
  /** Firebase Auth uid, taken from the callable's auth context. */
  uid: z.string(),
  /** The admin's email, when the auth token carried one. */
  email: z.string().optional(),
});

// ============================================================================
// Document
// ============================================================================

/**
 * Base schema for a scheduled send. Uses {@link FirestoreTimestampSchema} so
 * the admin and client modules can each extend it with their own timestamp
 * transform.
 */
export const EmailScheduledSendDocumentSchema = z
  .object({
    /** Course the send belongs to. The runner re-checks the role against it. */
    courseId: z.string().min(1),
    /** When the send should run. */
    scheduledFor: FirestoreTimestampSchema,
    /** Where the send is in its lifecycle. */
    status: EmailScheduledSendStatusSchema,
    /**
     * Identifies the one queued run allowed to send. Each run carries the id
     * it was queued with, and a run whose id no longer matches exits without
     * sending — so rescheduling never leaves the old time armed.
     *
     * A fresh random id rather than a counter: two concurrent reschedules
     * would both compute the same next count, and the loser's run would still
     * match.
     */
    runId: z.string().min(1),
    /**
     * The `enqueueadminemails` request body to run, without `courseId`.
     *
     * Opaque here because its schema belongs to the functions app; it is
     * validated against that schema when the send is saved and again when it
     * runs, so a request that a later deploy no longer accepts fails the send
     * rather than sending something different.
     */
    request: z.record(z.string(), z.unknown()),
    /**
     * Whether the runner rebuilds the autogenerated applicant lists the send
     * names before it enqueues, so a send scheduled days ahead goes to the
     * applicants as they stand at send time. Absent means no.
     */
    refreshApplicantLists: z.boolean().optional(),
    /** Admin who scheduled the send, and as whom it runs. */
    createdBy: ActorSchema,
    /** When the send was scheduled. */
    createdAt: FirestoreTimestampSchema,
    /** When the document last changed. */
    updatedAt: FirestoreTimestampSchema,
    /** Admin who cancelled the send. Present only once `cancelled`. */
    cancelledBy: ActorSchema.optional(),
    /** When the runner took the send. Present from `sending` on. */
    startedAt: FirestoreTimestampSchema.optional(),
    /** When the send reached `sent` or `failed`. */
    completedAt: FirestoreTimestampSchema.optional(),
    /**
     * The `enqueueadminemails` response, once `sent`. Opaque here for the same
     * reason as {@link request}.
     */
    result: z.record(z.string(), z.unknown()).optional(),
    /** Why the send failed. Present only once `failed`. */
    error: z.string().optional(),
  })
  .loose();

/** Inferred type for {@link EmailScheduledSendDocumentSchema}. */
export type EmailScheduledSendDocument = z.infer<
  typeof EmailScheduledSendDocumentSchema
>;

// ============================================================================
// Paths
// ============================================================================

/** Top-level collection holding scheduled sends. */
export const EMAIL_SCHEDULED_SEND_COLLECTION = 'email-scheduled-sends' as const;

/**
 * Path of a single scheduled send.
 *
 * @param params - Lookup parameters.
 * @param params.scheduledSendId - Firestore id of the scheduled send.
 * @return The document path `email-scheduled-sends/{scheduledSendId}`.
 * @throws Never.
 */
export function getEmailScheduledSendDocumentPath({
  scheduledSendId,
}: {
  scheduledSendId: string;
}): string {
  return `${EMAIL_SCHEDULED_SEND_COLLECTION}/${scheduledSendId}`;
}
